/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { STACCollection, STACItem } from '../api';

const DEFAULT_STAC_TEMPORAL_STEP = 'PT1H';

/**
 * Infers the temporal step (time resolution) from STAC collection metadata and items.
 * Attempts multiple strategies:
 * 1. Look for 'temporal-resolution' in collection properties
 * 2. Calculate from actual item timestamps
 * 3. Fall back to PT1H if unable to determine
 *
 * @param collection - STAC collection with metadata and items
 * @returns ISO 8601 duration string (e.g., 'PT5M', 'PT1H', 'P1D')
 */
export const inferTemporalStep = (
  collection: STACCollection | undefined,
  items?: STACItem[],
): string => {
  if (!collection) {
    return DEFAULT_STAC_TEMPORAL_STEP;
  }

  // Strategy 1: Check collection properties for explicit temporal resolution
  if (
    collection.properties &&
    typeof collection.properties === 'object' &&
    'temporal-resolution' in collection.properties
  ) {
    const resolution = collection.properties['temporal-resolution'];
    if (typeof resolution === 'string') {
      return resolution;
    }
  }

  // Strategy 2: Calculate from actual item timestamps
  const itemsToInferFrom =
    items && items.length >= 2 ? items : collection.features;
  if (itemsToInferFrom && itemsToInferFrom.length >= 2) {
    const diffs = calculateTimestampDifferences(itemsToInferFrom);
    if (diffs.length > 0) {
      const inferredStep = getConsistentTemporalStep(diffs);
      if (inferredStep) {
        return inferredStep;
      }
    }
  }

  // Fallback to default
  return DEFAULT_STAC_TEMPORAL_STEP;
};

/**
 * Calculates time differences (in milliseconds) between consecutive datetimes.
 * Items may be returned by the STAC API in any order; valid timestamps are sorted first.
 * @param items - STAC items to inspect
 * @returns Array of differences in milliseconds
 */
const calculateTimestampDifferences = (items: STACItem[]): number[] => {
  const diffs: number[] = [];
  const sortedTimes = items
    .map((item) => item.properties.datetime)
    .filter((datetime): datetime is string => Boolean(datetime))
    .map((datetime) => new Date(datetime).getTime())
    .filter((time) => Number.isFinite(time))
    .sort((left, right) => left - right);
  const maxSamples = Math.min(10, sortedTimes.length - 1);

  for (let i = 0; i < maxSamples; i += 1) {
    const diff = sortedTimes[i + 1] - sortedTimes[i];

    if (diff > 0) {
      diffs.push(diff);
    }
  }

  return diffs;
};

/**
 * Determines a consistent temporal step from time differences
 * If most differences are similar, returns the corresponding ISO 8601 duration
 * @param diffs - Array of time differences in milliseconds
 * @returns ISO 8601 duration or null if no consistent pattern found
 */
const getConsistentTemporalStep = (diffs: number[]): string | null => {
  if (diffs.length === 0) {
    return null;
  }

  // Group differences by similarity (within 5% tolerance)
  const sorted = [...diffs].sort((left, right) => left - right);
  const median = sorted[Math.floor(sorted.length / 2)];

  // Check if most differences are close to the median (within 5%)
  const tolerance = median * 0.05;
  const consistentDiffs = diffs.filter(
    (difference) => Math.abs(difference - median) <= tolerance,
  );

  if (consistentDiffs.length / diffs.length >= 0.7) {
    return millisecondsToISO8601Duration(median);
  }

  return null;
};

/**
 * Converts milliseconds to ISO 8601 duration string
 * @param ms - Duration in milliseconds
 * @returns ISO 8601 duration string
 */
export const millisecondsToISO8601Duration = (ms: number): string => {
  const seconds = Math.round(ms / 1000);

  if (seconds < 60) {
    return `PT${seconds}S`;
  }

  const minutes = seconds / 60;
  if (minutes < 60 && seconds % 60 === 0) {
    return `PT${Math.round(minutes)}M`;
  }

  const hours = seconds / 3600;
  if (hours < 24 && seconds % 3600 === 0) {
    return `PT${Math.round(hours)}H`;
  }

  const days = seconds / 86400;
  if (seconds % 86400 === 0) {
    return `P${Math.round(days)}D`;
  }

  // For non-standard intervals, use seconds
  return `PT${seconds}S`;
};
